import { useServices } from '@/app/providers/ServiceContext';
import { TenantPicker } from '@/presentation/components/molecules/TenantPicker';
import { CheckCircle, XCircle, Loader2, Home, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MagicLinkVerifyResponse, TenantSelection } from '@yawi/types';

interface MagicLinkVerifyProps {
  token: string;
  onSuccess: (response: MagicLinkVerifyResponse) => void;
  onSelectTenant?: (userId: string, tenantId: string) => Promise<MagicLinkVerifyResponse | void>;
}

export const MagicLinkVerify: React.FC<MagicLinkVerifyProps> = ({
  token,
  onSuccess,
  onSelectTenant,
}) => {
  const { userRepo, systemSettings } = useServices();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'select-tenant' | 'success' | 'error'>('verifying');
  const [error, setError] = useState<string>('');
  const [tenants, setTenants] = useState<TenantSelection[]>([]);
  const [loading, setLoading] = useState(false);
  const verifiedTokenRef = useRef<string | null>(null);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const branding = {
    appName: systemSettings?.branding?.appName || 'Hommi',
    primaryColor: systemSettings?.branding?.primaryColor || '#8B5CF6',
    logoUrl: systemSettings?.branding?.logoUrl || '',
  };

  useEffect(() => {
    if (!token || verifiedTokenRef.current === token) return;
    verifiedTokenRef.current = token;
    verifyToken();

    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }
    };
  }, [token]);

  const verifyToken = async () => {
    try {
      setStatus('verifying');
      const response = await userRepo.verifyMagicLink(token);

      if (response.requiresTenantSelection && response.tenants && response.tenants.length > 1) {
        setTenants(response.tenants);
        setStatus('select-tenant');
      } else {
        setStatus('success');
        redirectTimeoutRef.current = setTimeout(() => onSuccess(response), 1500);
      }
    } catch (err: any) {
      setStatus('error');
      setError(err.message || 'El enlace es inválido o ha expirado');
    }
  };

  const handleTenantSelect = async (tenant: TenantSelection) => {
    if (!onSelectTenant) return;
    setLoading(true);
    try {
      await onSelectTenant(tenant.userId, tenant.tenantId);
    } catch (err: any) {
      setError(err.message || 'Error al seleccionar la comunidad');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-100" />
      <div className="absolute inset-0">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-purple-300/50 rounded-full blur-[100px]" />
        <div className="absolute top-1/4 -right-32 w-[400px] h-[400px] bg-indigo-300/40 rounded-full blur-[80px]" />
        <div className="absolute -bottom-32 left-1/4 w-[600px] h-[600px] bg-violet-300/40 rounded-full blur-[120px]" />
      </div>

      {/* Content */}
      <div className="w-full flex items-center justify-center relative z-10 px-6">
        <div
          className="w-full max-w-md rounded-3xl p-8 text-center"
          style={{
            background: 'rgba(255, 255, 255, 0.6)',
            backdropFilter: 'blur(40px) saturate(180%)',
            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          }}
        >
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #8B5CF6, #6366F1)',
                boxShadow: '0 4px 16px rgba(139, 92, 246, 0.35)',
              }}
            >
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt="Logo" className="w-7 h-7 object-contain" />
              ) : (
                <ShieldCheck className="w-7 h-7 text-white" />
              )}
            </div>
          </div>

          {/* Verifying State */}
          {status === 'verifying' && (
            <div className="py-8">
              <Loader2 className="w-16 h-16 mx-auto text-purple-600 animate-spin mb-4" />
              <h2 className="text-xl font-bold text-slate-800 mb-2">Verificando enlace</h2>
              <p className="text-slate-500">Espera un momento mientras validamos tu acceso...</p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="py-8">
              <div className="w-20 h-20 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-2">Acceso verificado</h2>
              <p className="text-slate-500">Redirigiendo a tu cuenta...</p>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="py-8">
              <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-2">Enlace inválido</h2>
              <p className="text-slate-500 mb-6">{error}</p>
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm text-white transition-all hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(135deg, #8B5CF6, #6366F1)',
                  boxShadow: '0 4px 16px rgba(139, 92, 246, 0.35)',
                }}
              >
                <Home size={18} />
                Ir al inicio
              </button>
            </div>
          )}

          {/* Tenant Selection State */}
          {status === 'select-tenant' && (
            <TenantPicker tenants={tenants} loading={loading} onConfirm={handleTenantSelect} />
          )}
        </div>
      </div>
    </div>
  );
};
